import { MenuGroup } from '@components/common/Menu/GroupedMenu';
import { useUser } from '@hooks/useUser';
import { IApiRole } from '@styluxinc/api-schema';
import { useCallback, useMemo, useState } from 'react';
import { GroupedRoles, ROLE_OPTIONS } from '../utils';

export const useViewAsMenu = () => {
  const { user, setViewAsRoles } = useUser();

  const groupedRoles = useMemo(() => {
    return ROLE_OPTIONS.reduce((acc, option) => {
      acc[option.group] ??= [];
      acc[option.group].push({
        roleLabel: option.roleLabel,
        apiRole: option.apiRole,
      });
      return acc;
    }, {} as GroupedRoles);
  }, []);

  const [selectedApiRoles, setSelectedApiRoles] = useState<IApiRole[]>(
    (Array.from(user.roleSet) as IApiRole[]) || [],
  );

  const totalSelected = selectedApiRoles.length;

  const handleRoleChange = useCallback(
    (apiRole: IApiRole) => {
      setSelectedApiRoles((prev) => {
        const isSelected = prev.includes(apiRole);
        const selectedOption = ROLE_OPTIONS.find(
          (opt) => opt.apiRole === apiRole,
        );

        if (!selectedOption) return prev;

        // if no roles are selected, replace with the new role
        if (prev.length === 0) {
          setViewAsRoles([apiRole]);
          return [apiRole];
        }

        // find the group of the currently selected roles
        const currentGroup = ROLE_OPTIONS.find(
          (opt) => opt.apiRole === prev[0],
        )?.group;

        // if selecting from a different group
        // clear all others and select only the new role
        if (currentGroup !== selectedOption.group) {
          setViewAsRoles([apiRole]);
          return [apiRole];
        }

        // if selecting from the same group, toggle the role
        const newRoles = isSelected
          ? prev.filter((role) => role !== apiRole)
          : [...prev, apiRole];

        // prevent checking the only role in the group
        if (newRoles.length === 0) {
          return prev;
        }

        // update user context
        setViewAsRoles(newRoles);

        return newRoles;
      });
    },
    [setViewAsRoles],
  );

  const roleDisplayInfo = useMemo(() => {
    const displayRoles = selectedApiRoles.map((apiRole) => {
      const option = ROLE_OPTIONS.find((opt) => opt.apiRole === apiRole);
      return option ? `${option.group} ${option.roleLabel}` : apiRole;
    });

    return {
      summary: `${displayRoles[0]}${displayRoles.length > 1 ? ` +${displayRoles.length - 1}` : ''}`,
      label: displayRoles.join(', '),
    };
  }, [selectedApiRoles]);

  const tooltipTitle = useMemo(() => {
    if (selectedApiRoles.length === 1) {
      return 'View portal using different user roles';
    }

    return roleDisplayInfo.label;
  }, [selectedApiRoles, roleDisplayInfo]);

  const groupedMenuItems: MenuGroup[] = Object.entries(groupedRoles).map(
    ([group, roles]) => ({
      label: group,
      items: roles.map(({ roleLabel, apiRole }) => ({
        id: `${group}-${roleLabel}`,
        content: roleLabel,
        checked: selectedApiRoles.includes(apiRole),
        onClick: () => handleRoleChange(apiRole),
        disabled: selectedApiRoles.includes(apiRole) && totalSelected === 1,
        disabledTooltip: {
          title: 'At least one role must remain selected.',
        },
      })),
    }),
  );

  return {
    roleDisplayInfo,
    totalSelected,
    tooltipTitle,
    groupedMenuItems,
  };
};
